import { useLocale } from "next-intl";
import { useMutation } from "@tanstack/react-query";

import { syncSelections } from "@/api/coupons";
import { getSelectionEventId, useBetslipStore } from "@/stores/Betslip";

import { Selection, SportEvent } from "@/types/SportsApiResponses";

import { useAdvancedInterval } from "@/hooks/useAdvancedInterval";

const INTERVAL_SYNC = 5000;

interface SyncSelectionsResponse {
  selections: Selection[];
  sportEvents: SportEvent[];
  missingKeys: string[];
}

// A match is open for betting when at least one of its markets is — the same
// markets-based rule the event lists use (their local computeBetStatus): the raw
// betStatus flag is unreliable, so a feed row with no open market IS a
// suspension.
const hasOpenMarket = (event: SportEvent | undefined): boolean => {
  if (!event?.markets) return false;
  return Object.values(event.markets).some(
    (market) => market.status === "open"
  );
};

export const syncSelectionsOnSuccess = (data: SyncSelectionsResponse) => {
  const state = useBetslipStore.getState();

  const selectionsMapByKey = Object.fromEntries(
    data.selections.map((s) => [s.key, s])
  );

  const eventsMapByMatchId = Object.fromEntries(
    data.sportEvents.map((e) => [e.match.id, e])
  );

  Object.values(state.selectionMap).forEach((existingSelection) => {
    const syncedSelection = selectionsMapByKey[existingSelection.key];
    if (!syncedSelection) return;

    const matchId = Number(syncedSelection.key.split("|")[3]);
    // Fall back to the stored event: a FOUND selection must always apply its
    // fresh status. Skipping the update when the response event lookup missed
    // is exactly how a suspended-then-resumed selection kept its stale
    // "unavailable" warning forever.
    const event =
      eventsMapByMatchId[matchId] ??
      state.eventsMap[getSelectionEventId(existingSelection)];

    if (event) {
      state.updateSelection({ selection: syncedSelection, event });
    }
  });

  if (data.missingKeys && data.missingKeys.length > 0) {
    const missingKeysSet = new Set(data.missingKeys);

    Object.values(state.selectionMap).forEach((existingSelection) => {
      if (!missingKeysSet.has(existingSelection.key)) return;

      const matchId = Number(existingSelection.key.split("|")[3]);
      const freshEvent = eventsMapByMatchId[matchId];
      const event =
        freshEvent ??
        state.eventsMap[matchId] ??
        state.eventsMap[getSelectionEventId(existingSelection)];
      if (!event) return;

      // The feed still carries the match but without any open market (and our
      // key is gone) -> the match is SUSPENDED, not deleted: surface it as
      // on-hold ("askıya alındı") instead of "not found". When the suspension
      // lifts the key reappears and the found-branch above restores the
      // selection to open — the warning clears by itself.
      const suspended = freshEvent !== undefined && !hasOpenMarket(freshEvent);

      state.updateSelection({
        selection: {
          ...existingSelection,
          status: suspended ? "on_hold" : "unavailable",
        },
        event,
      });
    });
  }
};

export const useSyncSelection = () => {
  const locale = useLocale();

  const mutationFn = (ids: string[]) => {
    const state = useBetslipStore.getState();
    const selections = ids.map((id) => state.selectionMap[id]);
    return syncSelections({ lang: locale, selections });
  };

  return useMutation({ mutationFn });
};

export const useAutoSyncBetslipSelections = () => {
  const selections = useBetslipStore((s) => s.selections);
  const sync = useSyncSelection();

  useAdvancedInterval({
    delay: INTERVAL_SYNC,
    immediate: true,
    condition: selections.length > 0,
    callback: () =>
      sync.mutate(selections, { onSuccess: syncSelectionsOnSuccess }),
  });
};
